// Data Freshness Panel - Shows sync recency for all data sources
//
// Displays last sync times and staleness indicators.

import { useEffect, useState, useCallback } from 'react';
import { systemHealthService } from '../../services/systemHealth';
import type { DataFreshnessResponse, DataSourceFreshness } from '../../services/systemHealth';
import styles from './StatisticsTab.module.css';

const STATUS_COLORS: Record<string, string> = {
  fresh: '#16a34a',
  stale: '#f59e0b',
  critical: '#dc2626',
  unknown: '#94a3b8',
};

function FreshnessBadge({ status }: { status: string }) {
  const color = STATUS_COLORS[status] || STATUS_COLORS.unknown;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.25rem',
        padding: '0.125rem 0.5rem',
        borderRadius: '9999px',
        fontSize: '0.6875rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        color: 'white',
        background: color,
      }}
    >
      {status}
    </span>
  );
}

function formatStaleness(minutes: number | null): string {
  if (minutes == null) return 'Unknown';
  if (minutes < 60) return `${Math.round(minutes)}m ago`;
  if (minutes < 1440) return `${Math.round(minutes / 60)}h ago`;
  return `${Math.round(minutes / 1440)}d ago`;
}

function SourceRow({ source }: { source: DataSourceFreshness }) {
  return (
    <tr>
      <td>{source.source_name}</td>
      <td>
        <FreshnessBadge status={source.staleness_status} />
      </td>
      <td className={styles.numericCell}>
        {source.last_sync_at
          ? new Date(source.last_sync_at).toLocaleString()
          : 'Never'}
      </td>
      <td className={styles.numericCell}>
        {formatStaleness(source.staleness_minutes)}
      </td>
      <td className={styles.numericCell}>
        {source.record_count != null ? source.record_count.toLocaleString() : '—'}
      </td>
      <td>
        {source.last_job_status && (
          <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
            {source.last_job_status}
          </span>
        )}
        {source.last_job_error && (
          <span
            style={{
              display: 'block',
              fontSize: '0.6875rem',
              color: '#dc2626',
              maxWidth: '200px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={source.last_job_error}
          >
            {source.last_job_error}
          </span>
        )}
      </td>
    </tr>
  );
}

export default function DataFreshnessPanel() {
  const [data, setData] = useState<DataFreshnessResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await systemHealthService.getDataFreshness();
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load freshness data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) return <div className={styles.loading}>Loading data freshness...</div>;

  // Separate primary sources from per-table sync checkpoints
  const primarySources = data?.sources.filter((s) => !s.source_name.startsWith('Sync:')) ?? [];
  const checkpointSources = data?.sources.filter((s) => s.source_name.startsWith('Sync:')) ?? [];

  // Summary KPIs
  const freshCount = data?.sources.filter((s) => s.staleness_status === 'fresh').length ?? 0;
  const staleCount = data?.sources.filter((s) => s.staleness_status === 'stale').length ?? 0;
  const criticalCount = data?.sources.filter((s) => s.staleness_status === 'critical').length ?? 0;

  return (
    <>
      {error && <div className={styles.error}>{error}</div>}

      {data && (
        <>
          {/* Summary KPIs */}
          <div className={styles.kpiGrid}>
            <div className={styles.kpiCard}>
              <span className={styles.kpiLabel}>Fresh Sources</span>
              <span className={styles.kpiValue} style={{ color: '#16a34a' }}>
                {freshCount}
              </span>
            </div>
            <div className={styles.kpiCard}>
              <span className={styles.kpiLabel}>Stale Sources</span>
              <span className={styles.kpiValue} style={{ color: '#f59e0b' }}>
                {staleCount}
              </span>
            </div>
            <div className={styles.kpiCard}>
              <span className={styles.kpiLabel}>Critical Sources</span>
              <span className={styles.kpiValue} style={{ color: '#dc2626' }}>
                {criticalCount}
              </span>
            </div>
          </div>

          {/* Primary Sources */}
          <div className={styles.section}>
            <div className={styles.sectionTitle}>Primary Data Sources</div>
            <table className={styles.dataTable}>
              <thead>
                <tr>
                  <th>Source</th>
                  <th>Status</th>
                  <th className={styles.numericCell}>Last Sync</th>
                  <th className={styles.numericCell}>Staleness</th>
                  <th className={styles.numericCell}>Records</th>
                  <th>Job Status</th>
                </tr>
              </thead>
              <tbody>
                {primarySources.map((source) => (
                  <SourceRow key={source.source_name} source={source} />
                ))}
              </tbody>
            </table>
          </div>

          {/* Per-Table Sync Checkpoints */}
          {checkpointSources.length > 0 && (
            <div className={styles.section}>
              <div className={styles.sectionTitle}>
                Per-Table Sync Checkpoints ({checkpointSources.length})
              </div>
              <table className={styles.dataTable}>
                <thead>
                  <tr>
                    <th>Source</th>
                    <th>Status</th>
                    <th className={styles.numericCell}>Last Sync</th>
                    <th className={styles.numericCell}>Staleness</th>
                    <th className={styles.numericCell}>Records</th>
                    <th>Job Status</th>
                  </tr>
                </thead>
                <tbody>
                  {checkpointSources.map((source) => (
                    <SourceRow key={source.source_name} source={source} />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ textAlign: 'right', marginTop: '0.5rem' }}>
            <button
              onClick={loadData}
              style={{
                padding: '0.375rem 0.75rem',
                fontSize: '0.8125rem',
                fontWeight: 500,
                color: '#3b82f6',
                background: 'none',
                border: '1px solid #3b82f6',
                borderRadius: '6px',
                cursor: 'pointer',
              }}
            >
              Refresh
            </button>
          </div>
        </>
      )}
    </>
  );
}
